import { Typography } from '@components/typography';
import { Box, useTheme } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { useCallback, useContext, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { GraphsOutdatedNodesContext } from './GraphsOutdatedNodesProvider';
import { ChannelDetailContext } from './graph/ChannelDetailProvider';
import { GraphDataContext } from './graph/GraphDataProvider';

export const GraphsOutdatedNodesPreview = () => {
  const { palette } = useTheme();
  const { updateAllNodes } = useContext(GraphsOutdatedNodesContext);
  const { nodes } = useContext(GraphDataContext);

  const { channelNodes } = useContext(ChannelDetailContext);

  // Tracks notification keys via BehaviorSubject so callbacks/effect can read latest value
  // without subscribing to the React state (which would cause re-renders + dep churn).
  const [outdatedNotificationKeys$] = useState(new BehaviorSubject<string[]>([]));
  const { addNotification, removeNotification } = useNotifications();

  const handleUpdateAllOutdatedNodes = useCallback(
    async (ids: string[], key: string) => {
      await updateAllNodes(ids);
      outdatedNotificationKeys$
        .getValue()
        .forEach((outdatedKey) => removeNotification(outdatedKey));
      removeNotification(key);
    },
    [updateAllNodes, outdatedNotificationKeys$, removeNotification],
  );

  useEffect(() => {
    if (!channelNodes?.length || !nodes?.length) {
      return undefined;
    }
    const outdatedNodes = nodes.filter(({ hash, nodeClass }) => {
      if (nodeClass === 'VolumeFile' || nodeClass === 'VolumeDirectory') {
        return false;
      }
      const channelNode = channelNodes.find((findNode) => findNode.name === nodeClass);
      if (!channelNode) {
        return true;
      }
      const isNodeOutdated = channelNode?.hash
        ? hash !== channelNode.hash
        : hash !== channelNode?.hash;
      return isNodeOutdated;
    });
    const outdatedNodeIds = Array.from(new Set(outdatedNodes.map((n) => n.name)));

    const key = `${Date.now() * 1000}.${Math.random() * 10000}`;

    if (outdatedNodeIds.length > 0) {
      outdatedNotificationKeys$
        .getValue()
        .forEach((notification) => removeNotification(notification));
      outdatedNotificationKeys$.next([key]);

      addNotification({
        key,
        type: 'warning',
        link: (
          <Box>
            <Typography style={{ color: palette.warning.main }}>
              {outdatedNodeIds.length} outdated nodes detected in graph.
            </Typography>
            <Typography
              onClick={() => handleUpdateAllOutdatedNodes(outdatedNodeIds, key)}
              style={{
                textDecoration: 'underline',
                color: palette.warning.main,
                cursor: 'pointer',
              }}
            >
              Update all
            </Typography>
          </Box>
        ),
        duration: 100000,
      });
    }

    return () => {
      removeNotification(key);
    };
  }, [
    channelNodes,
    nodes,
    addNotification,
    removeNotification,
    palette.warning.main,
    handleUpdateAllOutdatedNodes,
    outdatedNotificationKeys$,
  ]);

  return null;
};
